
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, inject } from "@angular/core";
import { TaskState } from "../store/reducers/tasks.reducers";
import { Store } from "@ngrx/store";
import { getAuthUser } from "../store/selectors";
import { AuthService } from "../../common/providers/auth.service";
import { Observable, Subject, combineLatest, forkJoin } from "rxjs";
import { map, take } from "rxjs/operators";
import { CommonUtil, mobileonly } from "../../common/utils/common.utils";
import { Task, TaskComment, ICompact, Project, User, List, PendingOperation, Tag, SearchQuery, Location } from "../models";
import { AppConstants } from "../../common/utils/app-constants";
import { DatabaseReady } from "../../actions/app";
import { TaskUtils } from "../shared/task-utils";
import { LocalStorageService } from "./local-storage.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { FilesStorageService } from "./files-storage.service";
import { environment } from "../../../environments/environment";
import { IndexedDBService } from "./db/indexedDB.service";
import { StorageService } from "../../common/services/storage.service";


import { DatabaseStateBase } from "./database-state";

// Mechanical decomposition layer of DatabaseService (Q-02). Behavior-preserving.
export abstract class DatabaseCoreLayer extends DatabaseStateBase {
  protected deviceReady() {
    console.log("[SQLITE] deviceReady");

    // for iOS it's Library/NoCloud
    if (environment.isCordova) {
      this.storageLocation = cordova.file.dataDirectory;
    }

    this.setup().subscribe(() => {
      console.log("[SQLITE] database ready");
      this.store.dispatch(new DatabaseReady());
    });
  }

  protected setup(): Observable<any> {
    console.log("[SQLITE] setup");

    if (!CommonUtil.isOnNativeMobileDevice() && !environment.isElectron) {
      console.error("[SQLITE] init DB for unsupported platform");
      return;
    }
    const response = new Subject<any>();
    if (CommonUtil.isOnNativeMobileDevice()){
      try {
        this.sqliteDB = window.sqlitePlugin.openDatabase({
          name: this.KEY_SQLITE_DB,
          location: "default",
          androidDatabaseProvider: "system"
        }, (db) => {
          this.createTableIfNotExist().subscribe(() => {
            response.next("");
          });
        }, (error) => {
          console.log("[SQLITE] Open database ERROR: " + JSON.stringify(error));
          response.error(error);
        });
      } catch (e) {
        console.log(`[SQLITE] Error has occured - ${e}`);
        response.error(e);
      }
    } else if (environment.isElectron) {
      const DBElectronWrapper = window.DBElectronWrapper;
      console.log("[SQLITE] setup, DBElectronWrapper: ", DBElectronWrapper);

      if (DBElectronWrapper) {
        try {
          this.sqliteDB = new DBElectronWrapper(this.KEY_SQLITE_DB, (db) => {
            // Schema setup used to be skipped whenever a token was already present,
            // which meant an existing install never received a table added in a later
            // release. Every statement is CREATE TABLE IF NOT EXISTS, so running it
            // unconditionally is idempotent and costs one no-op pass on startup.
            this.createTableIfNotExist().subscribe(() => {
              response.next("");
            });
          }, (error) => {
            console.log("[SQLITE] Open database ERROR: " + JSON.stringify(error));
            response.error(error);
          });
        } catch (e) {
          console.log(`[SQLITE] Error has occured - ${e}`);
          response.error(e);
        }
      } else {
        console.error(`[SQLITE] Error has occured, DBElectronWrapper is empty`);
      }
    }
    return response.asObservable().pipe(take(1));
  }

  public fireDatabaseReadyIfInitialized(){
    if (this.sqliteDB && this.isDbInitialized) {
      this.store.dispatch(new DatabaseReady());
    }
  }

  /**
   * Implemented by the ops layer (which owns getMigrationIds). Declared here so
   * createTableIfNotExist can chain migrations without reaching downwards.
   */
  protected abstract runPendingMigrations(): Observable<any>;

  createTableIfNotExist(): Observable<any> {
    console.log("[SQLITE] createTableIfNotExist");

    const response = new Subject();

    this.sqliteDB.sqlBatch([
      // this.DROP_TASK_TABLE_SQL,
      // this.DROP_PROJECT_TABLE_SQL,
      // this.DROP_PENDING_OPERATIONS_TABLE_SQL,
      this.CREATE_AVATAR_TABLE_SQL,
      this.CREATE_TASK_TABLE_SQL,
      this.CREATE_PROJECT_TABLE_SQL,
      this.CREATE_LISTS_TABLE_SQL,
      this.CREATE_LOCATIONS_TABLE_SQL,
      this.CREATE_TASK_TAG_TABLE_SQL,
      this.CREATE_TAGS_TABLE_SQL,
      this.CREATE_PENDING_OPERATIONS_TABLE_SQL,
      this.CREATE_SAVE_SEARCHES_TABLE_SQL,
      this.CREATE_MIGRATION_TABLE_SQL
    ], () => {
      console.log(`[SQLITE] Tables created`);
      this.isDbInitialized = true;
      // Tables are created with IF NOT EXISTS, so a database made by an older
      // release keeps its original columns. Run the recorded migrations now that
      // the schema is up, then report ready either way - a migration failure must
      // not block startup.
      this.runPendingMigrations().subscribe({
        next: () => response.next(""),
        error: () => response.next("")
      });
    }, (error) => {
      console.log(`[SQLITE] Error has occured while creating tables`, error);
      response.error(error);
    });

    return response.asObservable().pipe(take(1));
  }


  public alterTableDetails(item: any ): Observable<any> {
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] alterTableDetails skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }
    const response = new Subject();
    this.sqliteDB.transaction((tx) => {
      // write to DB
      tx.executeSql(item.sql, [], (tx, result) => {
        tx.executeSql(this.CREATE_MIGRATION_SQL, [item.id, item.sql, item.table, new Date().toString()], (tx, result) => {
          console.log(`[SQLITE] Create Migration: success`);
        }, (tx, error) => {
          console.error(`[SQLITE] Create Migration: Error has occured while saving entry`, error);
          response.next(false);
        });
      }, (tx, error) => {
        console.error(`[SQLITE] alter table error: `, error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] alter table: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] alter table: Transaction success.`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public getTaskStatistics(params: any): Observable<any> {
    console.log("[SQLITE] getTaskStatistics, params: ", params);

    const response = new Subject();

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getTaskStatistics skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }
    let currentUserId;
    if (this.localStorageService.getUser()) {
      currentUserId = this.localStorageService.getUser().id;
    }
    if (!currentUserId) {
      return;
    }
    let resp = {};

    let time1;
    let time2;
    let time3;

    time1 = performance.now();
    console.log("[SQLITE] getTaskStatistics: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] getTaskStatistics: Transaction occured");
      time2 = performance.now();

      tx.executeSql(this.buildQueryForCounters(this.COUNT_ALL_TASKS_SQL, params), [currentUserId, currentUserId], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["all_tasks"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_ALL_TASKS_SQL error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_OPEN_SQL, params), [currentUserId, currentUserId], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["open"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_OPEN_SQL error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_NEW_SQL, params), [currentUserId, currentUserId], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["new"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_NEW_SQL error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_COMPLETED_SQL, params), [currentUserId, currentUserId], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["completed"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_COMPLETED_SQL error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_BY_ASSIGNED_SQL, params), [currentUserId, currentUserId], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["assigned_to_me"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_BY_ASSIGNED_SQL error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_BY_DUE_DATE_SQL, params), [currentUserId, currentUserId, TaskUtils.dateToday()], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["due_today"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_BY_DUE_DATE_SQL today error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_BY_DUE_DATE_SQL, params), [currentUserId, currentUserId, TaskUtils.dateTomorrow()], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["due_tomorrow"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_BY_DUE_DATE_SQL tomorrow error", error);
      });

      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_BY_DUE_DATE_RANGE_SQL, params), [currentUserId, currentUserId, TaskUtils.dateThisWeekStarts(), TaskUtils.dateThisWeekEnds()], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["due_this_week_count"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_BY_DUE_DATE_RANGE_SQL week error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_BY_AUTHOR_SQL, params), [currentUserId], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["created_by_me"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_BY_AUTHOR_SQL tomorrow error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_WATCH_SQL, params) + " '%" + currentUserId + "%'", [], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["watched_by_me"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_WATCH_SQL watch error", error);
      });
      tx.executeSql(this.buildQueryForCounters(this.COUNT_TASKS_MY_OVERDUE_SQL, params), [currentUserId, currentUserId, TaskUtils.dateYesterday()], (tx, result) => {
        const total_count = result.rows.item(0).cnt;
        resp["overdue"] = { total_count: total_count, due_tomorrow_count: 0, overdue_count: 0, completed_count: 0 };
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatistics COUNT_TASKS_MY_OVERDUE_SQL watch error", error);
      });
      if (environment.isElectron) {
        setTimeout(() => {
          console.log("[SQLITE] getTaskStatistics: Transaction success.", resp);
          response.next(resp);
        }, 1000);
      }
    }, (error) => {
      console.error("[SQLITE] getTaskStatistics: Transaction error", error);
      response.next(null);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] getTaskStatistics: Transaction success. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(resp);
    });

    return response.asObservable().pipe(take(1));
  }

  public getTaskStatisticsForTaskFilter(searchParams): Observable<any> {
    console.log("[SQLITE] getTaskStatisticsForTaskFilter, searchParams:", searchParams);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getTaskStatisticsForTaskFilter skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject();

    let resp = {};

    let time1;
    let time2;
    let time3;

    time1 = performance.now();
    console.log("[SQLITE] getTaskStatisticsForTaskFilter: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] getTaskStatisticsForTaskFilter: Transaction occured");
      time2 = performance.now();

      const query = this.buildQuery(this.COUNT_TOTAL_TASKS_SQL, 0, 0, searchParams);
      console.log("[SQLITE] getTaskStatisticsForTaskFilter, COUNT_TOTAL_TASKS_SQL query: ", query);
      tx.executeSql(query, [], (tx, result) => {
        resp["total_count"] = result.rows.item(0).cnt;
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatisticsForTaskFilter COUNT_TOTAL_TASKS_SQL error", error);
      });
      const completed_query = this.buildQuery(this.COUNT_TOTAL_COMPLETED_SQL, 0, 0, searchParams);
      console.log("[SQLITE] getTaskStatisticsForTaskFilter, COUNT_TOTAL_COMPLETED_SQL query: ", completed_query);
      tx.executeSql(completed_query, [], (tx, result) => {
        resp["completed_count"] = result.rows.item(0).cnt;
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatisticsForTaskFilter COUNT_TOTAL_COMPLETED_SQL error", error);
      });
      let overdueSearchParams = searchParams;
      if (overdueSearchParams["op[status_id]"] !== "c") {
        if (overdueSearchParams["v[status_id][]"] !== "1") {
          overdueSearchParams["op[status_id]"] = "o";
        }
        const overdue_query = this.buildQuery(this.COUNT_TOTAL_OVERDUE_SQL, 0, 0, overdueSearchParams);
        console.log("[SQLITE] getTaskStatisticsForTaskFilter, COUNT_TOTAL_OVERDUE_SQL query: ", overdue_query);
        tx.executeSql(overdue_query, [], (tx, result) => {
          resp["overdue_count"] = result.rows.item(0).cnt;
        }, (tx, error) => {
          console.error("[SQLITE] getTaskStatisticsForTaskFilter COUNT_TOTAL_OVERDUE_SQL error", error);
        });
      } else {
        resp["overdue_count"] = 0;
      }
      const due_tomorrow_query = this.buildQuery(this.COUNT_TOTAL_DUE_DATE_SQL, 0, 0, searchParams);
      console.log("[SQLITE] getTaskStatisticsForTaskFilter, COUNT_TOTAL_DUE_DATE_SQL query: ", due_tomorrow_query);
      tx.executeSql(due_tomorrow_query, [TaskUtils.dateTomorrow()], (tx, result) => {
        resp["due_tomorrow_count"] = result.rows.item(0).cnt;
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatisticsForTaskFilter COUNT_TOTAL_DUE_DATE_SQL error", error);
      });
      if (environment.isElectron) {
        setTimeout(() => {
          console.log("[SQLITE] getTaskStatisticsForTaskFilter: Transaction success.", resp);
          response.next(resp);
        }, 1000);
      }
    }, (error) => {
      console.error("[SQLITE] getTaskStatisticsForTaskFilter: Transaction error", error);
      response.next(null);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] getTaskStatisticsForTaskFilter: Transaction success. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`, resp);
      response.next(resp);
    });

    return response.asObservable().pipe(take(1));
  }

  public getTaskStatisticsForSearch(searchParams): Observable<any> {
    console.log("[SQLITE] getTaskStatisticsForSearch, searchParams:", searchParams);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getTaskStatisticsForSearch skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject();

    const currentUserId = this.localStorageService.getUser().id;

    let resp = {};

    let time1;
    let time2;
    let time3;

    time1 = performance.now();
    console.log("[SQLITE] getTaskStatisticsForSearch: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] getTaskStatisticsForSearch: Transaction occured");
      time2 = performance.now();

      const query = this.buildQueryForCountersInSearch(this.COUNT_ALL_TASKS_SQL, searchParams);
      console.log("[SQLITE] getTaskStatisticsForSearch, COUNT_ALL_TASKS_SQL query: ", query);
      tx.executeSql(query, [currentUserId, currentUserId], (tx, result) => {
        resp["total_count"] = result.rows.item(0).cnt;
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatisticsForSearch COUNT_ALL_TASKS_SQL error", error);
      });
      tx.executeSql(this.buildQueryForCountersInSearch(this.COUNT_TASKS_COMPLETED_SQL, searchParams), [currentUserId, currentUserId], (tx, result) => {
        resp["completed_count"] = result.rows.item(0).cnt;
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatisticsForSearch COUNT_TASKS_COMPLETED_SQL error", error);
      });
      let overdueSearchParams = searchParams;
      if (overdueSearchParams["op[status_id]"] !== "c") {
        overdueSearchParams["op[status_id]"] = "o";
        tx.executeSql(this.buildQueryForCountersInSearch(this.COUNT_TASKS_OVERDUE_SQL, overdueSearchParams), [currentUserId, currentUserId], (tx, result) => {
          resp["overdue_count"] = result.rows.item(0).cnt;
        }, (tx, error) => {
          console.error("[SQLITE] getTaskStatisticsForSearch COUNT_TASKS_OVERDUE_SQL error", error);
        });
      } else {
        resp["overdue_count"] = 0;
      }
      tx.executeSql(this.buildQueryForCountersInSearch(this.COUNT_TASKS_BY_DUE_DATE_SQL, searchParams), [currentUserId, currentUserId, TaskUtils.dateTomorrow()], (tx, result) => {
        resp["due_tomorrow_count"] = result.rows.item(0).cnt;
      }, (tx, error) => {
        console.error("[SQLITE] getTaskStatisticsForSearch COUNT_TASKS_BY_DUE_DATE_SQL error", error);
      });
      if (environment.isElectron) {
        setTimeout(() => {
          console.log("[SQLITE] getTaskStatisticsForSearch: Transaction success.", resp);
          response.next(resp);
        }, 1000);
      }
    }, (error) => {
      console.error("[SQLITE] getTaskStatisticsForSearch: Transaction error", error);
      response.next(null);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] getTaskStatisticsForSearch: Transaction success. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`, resp);
      response.next(resp);
    });

    return response.asObservable().pipe(take(1));
  }

  public saveOrUpdateTasks(tasks: Task[]): Observable<boolean>  {
    console.log("[SQLITE] saveOrUpdateTasks, count: " + tasks.length);

    const response = new Subject<boolean>();

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] saveOrUpdateTasks skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    if (!tasks || tasks.length === 0){
      return;
    }

    const maybeExistentIds = tasks.map(task => task.id);

    let toCreateCounter = 0;
    let toUpdateCounter = 0;

    let time1;
    let time2;
    let time3;

    // Step 1:
    // find existing tasks
    time1 = performance.now();
    console.log("[SQLITE] saveOrUpdateTasks: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] saveOrUpdateTasks: Transaction occured");
      time2 = performance.now();
      const query = this.FIND_TASKS_BY_ID_SQL + "('" + maybeExistentIds.join("','") + "')";

      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] saveOrUpdateTasks found tasks in DB: ", result.rows.length);

          let existentIds = [];
          for (let i = 0; i < result.rows.length; i++) {
            let item = result.rows.item(i);
            existentIds.push(item.id);
          }

          // Step 2: insert/update tasks in DB
          //
          maybeExistentIds.forEach((id, index) => {
            let query;
            let params;

            const task = tasks[index];

            if (existentIds.includes(id)) {
              params = this.paramsForCreateOrUpdateTask(task, false);
              query = this.UPDATE_TASK_SQL;
              ++toUpdateCounter;
            } else {
              params = this.paramsForCreateOrUpdateTask(task, true);
              query = this.CREATE_TASK_SQL;
              ++toCreateCounter;
            }

            // write to DB
            tx.executeSql(query, params, (tx, result) => {
              // console.log(`[SQLITE] saveOrUpdateTasks: Add or Update task`, result);
            }, (tx, error) => {
              console.error(`[SQLITE] saveOrUpdateTasks: Error has occured while saving task`, error);
            });
            task.tags.forEach(tag => {
              tx.executeSql(this.CREATE_TAG_IF_NOT_EXIST_SQL, [tag.id, tag.name, tag.id], (tx, result) => {
                // console.log(`[SQLITE] saveOrUpdateTasks: Add or Update tag`, result);
              }, (tx, error) => {
                console.error(`[SQLITE] saveOrUpdateTasks: Error has occured while saving task`, error);
              });
              tx.executeSql(this.CREATE_TASK_TAG_IF_NOT_EXIST_SQL, [task.id, tag.id, task.id, tag.id], (tx, result) => {
                // console.log(`[SQLITE] saveOrUpdateTasks: Add or Update task`, result);
              }, (tx, error) => {
                console.error(`[SQLITE] saveOrUpdateTasks: Error has occured while saving task`, error);
              });
            });
            if (environment.isElectron) {
              response.next(true);
            }
          });
        }, (tx, error) => {
          console.error("[SQLITE] saveOrUpdateTasks: Error has occured while finding tasks", error);
        }
      );
    }, (error) => {
      console.error("[SQLITE] saveOrUpdateTasks: Transaction error", error);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] saveOrUpdateTasks: Transaction success. CreatedTasks: ${toCreateCounter}, UpdatedTasks: ${toUpdateCounter}. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateTask(task: Task, removedTagsIds: number[]): Observable<boolean>{
    console.log("[SQLITE] updateTask, task & removedTagsIds: ", task, removedTagsIds);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateTask skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    const params = this.paramsForCreateOrUpdateTask(task, false);
    const query = this.UPDATE_TASK_SQL;

    this.sqliteDB.transaction((tx) => {
      // write to DB
      tx.executeSql(query, params, (tx, result) => {
        console.log(`[SQLITE] updateTask: update task success`, result);
      }, (tx, error) => {
        console.error(`[SQLITE] updateTask: Error has occured while updating task`, error);
      });
      removedTagsIds.forEach(removedTagId => {
        tx.executeSql(this.DELETE_TASK_TAG_BY_TAKS_AND_TAG_SQL, [task.id, removedTagId], (tx, result) => {
          console.log(`[SQLITE] updateTask: Delete task`, result);
        }, (tx, error) => {
          console.error(`[SQLITE] updateTask: Error has occured while updating task`, error);
        });
      });
      task.tags.forEach(tag => {
        tx.executeSql(this.CREATE_TAG_IF_NOT_EXIST_SQL, [tag.id, tag.name, tag.id], (tx, result) => {
          console.log(`[SQLITE] updateTask: Add or Update tag`, result);
        }, (tx, error) => {
          console.error(`[SQLITE] updateTask: Error has occured while updating task`, error);
        });
        tx.executeSql(this.CREATE_TASK_TAG_IF_NOT_EXIST_SQL, [task.id, tag.id, task.id, tag.id], (tx, result) => {
          console.log(`[SQLITE] updateTask: Add or Update task`, result);
        }, (tx, error) => {
          console.error(`[SQLITE] updateTask: Error has occured while updating task`, error);
        });
      });
      if (environment.isElectron) {
        setTimeout(() => {
          response.next(true);
        }, 1000);
      }
    }, (error) => {
      console.error("[SQLITE] updateTask: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] updateTask: Transaction success.`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public addNewTask(task: Task): Observable<boolean>{
    console.log("[SQLITE] addNewTask, task: ", task);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addNewTask skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    let time1;
    let time2;
    let time3;

    const params = this.paramsForCreateOrUpdateTask(task, true);

    console.log("[SQLITE] addNewTask, params & tags: ", params, task.tags);

    time1 = performance.now();
    this.sqliteDB.transaction((tx) => {
      time2 = performance.now();

      tx.executeSql(this.CREATE_TASK_SQL, params, (tx, result) => {
        console.log(`[SQLITE] addTask: Add task success`, result);
      }, (tx, error) => {
        console.error(`[SQLITE] addTask: Error has occured while adding task`, error);
      });
      task.tags.forEach(tag => {
        tx.executeSql(this.CREATE_TAG_IF_NOT_EXIST_SQL, [tag.id, tag.name, tag.id], (tx, result) => {
          console.log(`[SQLITE] addTask: Add or Update tag`, result);
        }, (tx, error) => {
          console.error(`[SQLITE] addTask: Error has occured while adding task`, error);
        });
        tx.executeSql(this.CREATE_TASK_TAG_IF_NOT_EXIST_SQL, [task.id, tag.id, task.id, tag.id], (tx, result) => {
          console.log(`[SQLITE] addTask: Add or Update task`, result);
        }, (tx, error) => {
          console.error(`[SQLITE] addTask: Error has occured while adding task`, error);
        });
      });
      if (environment.isElectron) {
        response.next(true);
      }
    }, (error) => {
      console.error("[SQLITE] addNewTask: Transaction error", error);
      response.next(false);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] addNewTask: Transaction success. TTAT ${(time2 - time1).toFixed()}  TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public replaceTask(previousTaskId: number, task: Task): Observable<boolean>  {
    console.log("[SQLITE] replaceTask, previousTaskId & task: ", previousTaskId, task);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] replaceTask skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    const params = this.paramsForCreateOrUpdateTask(task, true);
    params.push(previousTaskId);
    console.log("[SQLITE] replaceTask, params: ", params);

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.REPLACE_TASK_SQL, params, (tx, result) => {
        console.log(`[SQLITE] replaceTask: replace task success`, result);
        response.next(true);
      }, (tx, error) => {
        console.error(`[SQLITE] replaceTask: Error has occured while replace task`, error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] replaceTask: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] replaceTask: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public getTask(id: number): Observable<Task>{
    console.log("[SQLITE] getTask, id: " + id);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getTask skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<Task>();

    let time1;
    let time2;
    let time3;

    let task;

    time1 = performance.now();
    this.sqliteDB.transaction((tx) => {
      time2 = performance.now();

      tx.executeSql(this.FIND_TASK_BY_ID_SQL, [id],
        (tx, result) => {
          if (result.rows.length > 0) {
            let item = result.rows.item(0);
            task = this.buildTaskModelFromDBRecord(item);
            console.log("[SQLITE] getTask, task found: ", task);

            tx.executeSql(this.SELECT_TAGS_FOR_TASK, [id],
              (tx, result) => {
                if (result.rows.length > 0) {
                  for (let i = 0; i < result.rows.length; i++) {
                    let item = result.rows.item(i);
                    task.tags.push(item);
                  }
                  console.log("[SQLITE] getTask, tags found: ", task.tags);
                }
                if (environment.isElectron) {
                  setTimeout(() => {
                    console.log("[SQLITE] getTask, found: ", task);
                    response.next(task);
                  }, 1000);
                }
              }, (tx, error) => {
                task = null;
                console.error("[SQLITE] getTask Error has occured while finding task's tags", error);
              }
            );
          } else {
            console.log("[SQLITE] getTask, nothing found");
            response.next(undefined);
          }
        }, (error) => {
          console.error("[SQLITE] getTask Error has occured while finding task", error);
          response.next(undefined);
        });
    }, (error) => {
      console.error("[SQLITE] getTask: Transaction error", error);
      response.next(undefined);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] getTask: Transaction success. TTAT ${(time2 - time1).toFixed()}  TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(task);
    });

    return response.asObservable().pipe(take(1));
  }

  public getTasks(offset: number, limit: number, params: any): Observable<Task[]>{
    console.log("[SQLITE] getTasks, offset: " + offset + ", limit: " + limit + ", params: ", params);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getTasks skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    if (!params) {
      console.log("[SQLITE] getTasks skip: params is null.");
      return;
    }

    const response = new Subject<Task[]>();

    let time1;
    let time2;
    let time3;

    let tasks: Task[] = [];
    let tasksHash = {};
    let errMessage;

    time1 = performance.now();
    this.sqliteDB.transaction((tx) => {
      time2 = performance.now();

      const query = this.buildQuery(this.FIND_TASKS_SQL, offset, limit, params);
      console.log("[SQLITE] getTasks, query: ", query);

      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] getTasks, results: ", result);
          if (result.rows) {
            console.log("[SQLITE] getTasks, results rows: ", result.rows);
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              let task = this.buildTaskModelFromDBRecord(item);
              tasks.push(task);
              tasksHash[task.id] = task;
            }
            console.log("[SQLITE] getTasks tasks array: ", tasks);
            const query = this.SELECT_TAGS_FOR_TASKS + "('" + Object.keys(tasksHash).join("','") + "')";
            console.log("[SQLITE] getTasks tags, query: ", query);
            tx.executeSql(query, [],
              (tx, result) => {
                console.log("[SQLITE] getTasks tags, results: ", result);
                if (result.rows.length > 0) {
                  console.log("[SQLITE] getTasks tags, results rows: ", result.rows);
                  for (let i = 0; i < result.rows.length; i++) {
                    const item = result.rows.item(i);
                    const task = tasksHash[item.task_id];
                    task.tags.push({id: item.id, name: item.name});
                    console.log("[SQLITE] getTasks tasks tags array: ", task.tags);
                  }
                  console.log("[SQLITE] getTasks tasks with tags array: ", tasks);
                }
              }, (tx, error) => {
                console.error("[SQLITE] getTasks Error has occured while finding tasks tags", error);
                response.error(error);
              }
            );
          }
          console.log("[SQLITE] getTasks fetched tasks: ", tasks.length);
          if (environment.isElectron) {
            response.next(tasks);
          }
        }, (tx, error) => {
          console.error("[SQLITE] getTasks Error has occured while searching tasks:", error);
          response.error(error);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getTasks: Transaction error", error);
      response.error(error);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] getTasks: Transaction success. TTAT ${(time2 - time1).toFixed()}  TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`, tasks);
      response.next(tasks);
    });

    return response.asObservable().pipe(take(1));
  }

  public getTagsWithTasksCount(): Observable<any>{
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getTagsWithTasksCount skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    console.log("[SQLITE] getTagsWithTasksCount");

    const response = new Subject<any>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_ALL_TAGS_WITH_TASKS_COUNT_SQL, [],
        (tx, result) => {
          let tagsStats: Tag[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              tagsStats.push(new Tag(item));
            }
          }
          console.log("[SQLITE] getTagsWithTasksCount tagsStats", tagsStats);
          response.next(tagsStats);
        }, (tx, error) => {
          console.error("[SQLITE] getTagsWithTasksCount Error has occured while groupping tags", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getTagsWithTasksCount: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getTagsWithTasksCount: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  public getAllTags(): Observable<ICompact[]>{
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getAllTags skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    console.log("[SQLITE] getAllTags");

    const response = new Subject<ICompact[]>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_ALL_TAGS, [],
        (tx, result) => {
          let allTags: ICompact[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              allTags.push(item);
            }
          }
          console.log("[SQLITE] getAllTags allTags", allTags);
          response.next(allTags);
        }, (tx, error) => {
          console.error("[SQLITE] getAllTags Error has occured", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getAllTags: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getAllTags: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  public getSearchTags(searchtext: String): Observable<Tag[]>{
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getSearchTags skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    console.log("[SQLITE] getSearchTags searchtext", searchtext);

    const response = new Subject<Tag[]>();

    this.sqliteDB.readTransaction((tx) => {
      const query = this.SEARCH_TAGS_FOR_TASKS + `'%${DatabaseCoreLayer.sqlText(searchtext)}%'`;
      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] getSearchTags found", result.rows.length);
          let tags: Tag[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              tags.push(new Tag(item));
            }
          }
          console.log("[SQLITE] getSearchTags tags found", tags);
          response.next(tags);
        }, (tx, error) => {
          console.error("[SQLITE] getSearchTags Error has occured", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getSearchTags: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getSearchTags: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  public replaceTagId(oldId: number, newId: number): Observable<boolean>{
    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] replaceTagId skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    console.log("[SQLITE] replaceTagId oldId & newId", oldId, newId);

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.REPLACE_TAG_ID_SQL, [newId, oldId],
        (tx, result) => {
          console.log("[SQLITE] replaceTagId Tags done");
        }, (tx, error) => {
          console.error("[SQLITE] replaceTagId Tags Error has occured", error);
        }
      );
      tx.executeSql(this.REPLACE_TASK_TAG_ID_SQL, [newId, oldId],
        (tx, result) => {
          console.log("[SQLITE] replaceTagId TagsTask done");
        }, (tx, error) => {
          console.error("[SQLITE] replaceTagId TagsTask Error has occured", error);
        }
      );
    }, (error) => {
      console.error("[SQLITE] replaceTagId: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] replaceTagId: Transaction success.`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  protected buildTaskModelFromDBRecord(item){
    let task = new Task(JSON.parse(item.task));

    // if (item.assigned_to) {
    //   task.assigned_to
    // }

    if (task.due_date && task.due_date.getTime() === TaskUtils.dueDateOrStartDateNull().getTime()) {
      task.due_date = null;
    }
    if (task.start_date && task.start_date.getTime() === TaskUtils.dueDateOrStartDateNull().getTime()) {
      task.start_date = null;
    }

    if (item.list_id) {
      task.list = {id: item.list_id, name: item.list_name};
    } else {
      task.list = null;
    }
    if (item.location_id) {
      task.location = {id: item.location_id, name: item.location_name};
    } else {
      task.location = null;
    }
    task.tags = []; // will be filled on next query

    return task;
  }

  protected buildTaskJson(task: Task){
    return JSON.stringify(task, (replacerKey, replacerValue) => {
      if (replacerKey === "tags" || replacerKey === "list" || replacerKey === "location") {
        // 'tags' & 'list' & 'location' are stored in separate tables
        return undefined;
      }
      return replacerValue;
    });
  }

  protected paramsForCreateOrUpdateTask(task: Task, isCreate: boolean){
    if (!task.due_date) {
      task.due_date = TaskUtils.dueDateOrStartDateNull();
    }
    if (!task.start_date) {
      task.start_date = TaskUtils.dueDateOrStartDateNull();
    }

    if (task.estimated_hours && !task.spent_hours) {
      task.spent_hours = "00:00";
    }

    const taskJson = this.buildTaskJson(task);
    const unifiedParams = [task.status.id,
                          task.status.name,
                          task.author.id,
                          task.assigned_to ? task.assigned_to.id : 0,
                          TaskUtils.dateToSearchableDBDate(task.start_date),
                          TaskUtils.dateToSearchableDBDate(task.due_date),
                          task.subject,
                          task.priority.id,
                          task.description,
                          task.project.id,
                          JSON.stringify(task.comments),
                          task.list ? task.list.id : null,
                          task.location ? task.location.id : null,
                          task.tags && task.tags.length > 0 ? JSON.stringify(task.tags.map(t  => t.id.toString())) : null, // tags_ids
                          task.watchers && task.watchers.length > 0 ? JSON.stringify(task.watchers.map(t  => t.id.toString())) : null,
                          task.repeat,
                          task.external_url ? task.external_url : null,
                          taskJson,
                          TaskUtils.dateToSearchableDBDateTime(task.created_on)];
    if (isCreate) {
      return [task.id, ...unifiedParams];
    }
    return [...unifiedParams, task.id];
  }
  // for get tasks

  // ---------------------------------------------------------------------------
  // SQL literal guards.
  //
  // The query builders below assemble SQL by concatenation, and several of the
  // values come straight from user-controlled search params (free-text search,
  // tag/list/location ids, sort field). tx.executeSql() does support bound
  // parameters, but the builders return a bare string while each caller supplies
  // its own positional bind array, so binding here would mean changing four
  // signatures and ~20 call sites. Until that refactor happens, every value
  // interpolated into SQL must go through one of these guards.
  // ---------------------------------------------------------------------------

  /** Escapes a value for use inside a single-quoted SQL string literal. */
  protected static sqlText(value: any): string {
    return String(value ?? "").replace(/\0/g, "").replace(/'/g, "''");
  }

  /** Returns value as an integer, or null when it is not one. */
  protected static sqlInt(value: any): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  }

  /** Returns value as a yyyy-MM-dd date literal, or null when it is not one. */
  protected static sqlDate(value: any): string | null {
    const asString = String(value ?? "");
    return /^\d{4}-\d{2}-\d{2}$/.test(asString) ? asString : null;
  }

  /** Returns value only when it is one of the permitted column names. */
  protected static sqlColumn(value: any, allowed: string[]): string | null {
    const asString = String(value ?? "");
    return allowed.indexOf(asString) !== -1 ? asString : null;
  }

  /** Columns the caller is allowed to sort by. */
  protected static readonly SORTABLE_COLUMNS = [
    "subject", "priority", "status_id", "due_date", "start_date", "created_on",
    "updated_on", "assigned_to", "author", "id"
  ];

  /** Columns free-text search is allowed to target. */
  protected static readonly SEARCHABLE_COLUMNS = ["subject", "description", "comments"];

  protected buildQuery(baseQuery: string, offset: number, limit: number, params: any){
    let query = baseQuery;

    if (params) {
      query = this.buildAssigneeAndAuthorQuery(query, params);

      // Search text
      if (params["op[fulltext][]"] && params["v[fulltext][]"]) {
        query = this.buildSearchQuery(query, params);
      }

      // Status
      const statusId = params["op[status_id]"];
      if (statusId) {
        if (statusId === "c") {
          query += ` AND trim(lower(status_name)) = 'completed'`;
        } else if (statusId === "o") {
          query += ` AND trim(lower(status_name)) IN ('in progress', 'new')`;
        } else if ( statusId === "=" && params["v[status_id][]"] === "1") {
          query += ` AND trim(lower(status_name)) = 'new'`;
        }
      }

      // Due date
      const opDueDate = params["op[due_date]"];
      if (opDueDate) {
        if (opDueDate === "w") { // this week
          query += ` AND date(due_date) BETWEEN '${TaskUtils.dateThisWeekStarts()}' AND '${TaskUtils.dateThisWeekEnds()}'`;
        } else if (opDueDate === "=") { // today or tomorrow
          const vDueDate = DatabaseCoreLayer.sqlDate(params["v[due_date][]"]);
          if (vDueDate) {
            query += ` AND date(due_date) = '${vDueDate}'`;
          }
        } else if (opDueDate === "<=") {
          const vDueDate = DatabaseCoreLayer.sqlDate(params["v[due_date][]"]);
          if (vDueDate) {
            query += ` AND date(due_date) <= '${vDueDate}'`;
          }
        }
      }

      // Tags
      const opTags = params["op[tags]"];
      if (opTags) {
        if (opTags === "=") {
          const vTags = DatabaseCoreLayer.sqlText(params["v[tags][]"]);  // tag id
          query += ` AND tags_ids LIKE '%"${vTags}"%'`;
        }
      }

      // Lists
      const opList = params["op[list]"];
      if (opList) {
        if (opList === "=") {
            const vList = DatabaseCoreLayer.sqlInt(params["v[list][]"]);  // list id
            if (vList !== null) {
              query += ` AND list_id = ${vList}`;
            }
        }
      }

      // Lists
      const opLocation = params["op[location]"];
      if (opLocation) {
        if (opLocation === "=") {
            const vList = DatabaseCoreLayer.sqlInt(params["v[location][]"]);  // list id
            if (vList !== null) {
              query += ` AND location_id = ${vList}`;
            }
        }
      }

      // Sort
      const sortBy = params["sort"]; // due_date:asc, priority:desc, subject:asc
      if (sortBy) {
        const sortByParts = sortBy.split(":");
        const sortByField = sortByParts[0];
        const sortByDirection = sortByParts[1].toUpperCase();
        if (sortByField === "due_date") {
          query += " ORDER BY date(due_date)";
        } else if (sortByField === "start_date") {
          query += " ORDER BY date(start_date)";
        } else if (sortByField === "created_on") {
          query += " ORDER BY date(created_on)";
        } else if (sortByField === "subject") {
          query += " ORDER BY subject COLLATE NOCASE";
        } else if (sortByField === "status") {
          query += " ORDER BY status_id";
        } else {
          // A column name cannot be a bound parameter, so it has to be allowlisted.
          const safeSortField = DatabaseCoreLayer.sqlColumn(sortByField, DatabaseCoreLayer.SORTABLE_COLUMNS);
          query += safeSortField ? ` ORDER BY ${safeSortField}` : " ORDER BY date(due_date)";
        }
        query += sortByDirection === "DESC" ? " DESC" : " ASC";
      } else {
        // default sort
        query += " ORDER BY date(due_date) ASC";
      }
    }

    const safeLimit = DatabaseCoreLayer.sqlInt(limit);
    if (safeLimit !== null && safeLimit > 0) {
      query += ` LIMIT ${safeLimit}`;
    }
    const safeOffset = DatabaseCoreLayer.sqlInt(offset);
    if (safeOffset !== null && safeOffset > 0) {
      query += ` OFFSET ${safeOffset}`;
    }

    return query;
  }
  // for counts

  protected buildQueryForCounters(baseQuery: string, params: any){
    let query = baseQuery;
    return query;
  }
  // for counts in search

  protected buildQueryForCountersInSearch(baseQuery: string, params: any){
    let query = baseQuery;

    if (params) {
      // Search text
      if (params["op[fulltext][]"] && params["v[fulltext][]"]) {
        query = this.buildSearchQuery(query, params);
      }

      // Status
      const statusId = params["op[status_id]"];
      if (statusId) {
        if (statusId === "c") {
          query += ` AND trim(lower(status_name)) = 'completed'`;
        } else if (statusId === "o") {
          query += ` AND trim(lower(status_name)) IN ('in progress', 'new')`;
        } else if ( statusId === "=" && params["v[status_id][]"] === "1") {
          query += ` AND trim(lower(status_name)) = 'new'`;
        }
      }

       // Tags
       const opTags = params["op[tags]"];
       if (opTags) {
         if (opTags === "=") {
           const vTags = DatabaseCoreLayer.sqlText(params["v[tags][]"]);  // tag id
           query += ` AND tags_ids LIKE '%"${vTags}"%'`;
         }
       }

       // Lists
       const opList = params["op[list]"];
       if (opList) {
         if (opList === "=") {
             const vList = DatabaseCoreLayer.sqlInt(params["v[list][]"]);  // list id
             if (vList !== null) {
               query += ` AND list_id = ${vList}`;
             }
         }
       }

       // Locations
       const opLocation = params["op[location]"];
       if (opLocation) {
         if (opLocation === "=") {
             const vList = DatabaseCoreLayer.sqlInt(params["v[location][]"]);  // list id
             if (vList !== null) {
               query += ` AND location_id = ${vList}`;
             }
         }
       }
    }

    return query;
  }

  protected buildAssigneeAndAuthorQuery(baseQuery: string, params: any){
    let query = baseQuery;

    if (params["v[assigned_to_id][]"]) {
      const assignedToId = params["v[assigned_to_id][]"];
      const assignedToID = DatabaseCoreLayer.sqlInt(
        assignedToId === "me" ? this.localStorageService.getUser().id : assignedToId);
      if (assignedToID !== null) {
        query += ` AND assigned_to = ${assignedToID}`;
      }
    } else if (params["v[author_or_assigned][]"]) {
      const authorOrAssigned = params["v[author_or_assigned][]"];
      const authorIDOrAssignedToID = DatabaseCoreLayer.sqlInt(
        authorOrAssigned === "me" ? this.localStorageService.getUser().id : authorOrAssigned);
      if (authorIDOrAssignedToID !== null) {
        query += ` AND (author = ${authorIDOrAssignedToID} OR assigned_to = ${authorIDOrAssignedToID})`;
      }
    } else if (params["v[author_assigned_or_watcher][]"]) {
      const authorOrAssigned = params["v[author_assigned_or_watcher][]"];
      const authorIDOrAssignedToID = DatabaseCoreLayer.sqlInt(
        authorOrAssigned === "me" ? this.localStorageService.getUser().id : authorOrAssigned);
      if (authorIDOrAssignedToID !== null) {
        query += ` AND (author = ${authorIDOrAssignedToID} OR assigned_to = ${authorIDOrAssignedToID} OR watcher_ids LIKE '%${authorIDOrAssignedToID}%')`;
      }
    }

    if (params["v[author_id][]"]) {
      const author = params["v[author_id][]"];
      const authorID = DatabaseCoreLayer.sqlInt(
        author === "me" ? this.localStorageService.getUser().id : author);
      if (authorID !== null) {
        if (params["op[author_id]"] === "!") {
          query += ` AND author != ${authorID}`;
        } else {
          query += ` AND author = ${authorID}`;
        }
      }
    }

    if (params["v[watcher_id][]"]) {
      const watcher = params["v[watcher_id][]"];
      const watcherID = DatabaseCoreLayer.sqlInt(
        watcher === "me" ? this.localStorageService.getUser().id : watcher);
      if (watcherID !== null) {
        query += ` AND watcher_ids LIKE '%${watcherID}%'`;
      }
    }
    return query;
  }

  protected buildSearchQuery(baseQuery: string, params: any){
    let query = baseQuery;

    const paramsToSearchBy = params["op[fulltext][]"];
    query += " AND ";
    const searchText = DatabaseCoreLayer.sqlText(params["v[fulltext][]"][0]);
    paramsToSearchBy.forEach((sOp, sOpIndex) => {
      let formalizedOp;
      if (sOp === "subj") {
        formalizedOp = "subject";
      } else if (sOp === "desc") {
        formalizedOp = "description";
      } else if (sOp === "comm") {
        formalizedOp = "comments";
      } else {
        // Unmapped operators used to be dropped straight into the column position.
        // Only a known column may appear there; anything else falls back to subject.
        formalizedOp = DatabaseCoreLayer.sqlColumn(sOp, DatabaseCoreLayer.SEARCHABLE_COLUMNS) || "subject";
      }

      // 1st element
      if (sOpIndex === 0 && paramsToSearchBy.length > 1) {
        query += "(";
      }
      //
      if (sOpIndex === 0) {
        query += `${formalizedOp} like '%${searchText}%'`;
      } else {
        query += ` OR ${formalizedOp} like '%${searchText}%'`;
      }
      // last elemement
      if (sOpIndex === paramsToSearchBy.length - 1 && paramsToSearchBy.length > 1) {
        query += ")";
      }
    });

    return query;
  }

  public removeTasks(tasksIds: number[]): Observable<any>{
    console.log("[SQLITE] removeTasks, tasksIds: ", tasksIds);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] removeTasks skip: sqliteDB or isDbInitialized is not initialized.");
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      let query = this.DELETE_TASKS_SQL + "('" + tasksIds.join("','") + "')";
      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] removeTasks success", result);
        }, (tx, error) => {
          console.error("[SQLITE] removeTasks Error has occured", error);
        }
      );
      query = this.DELETE_TASK_TAG_BY_TASK_SQL + "('" + tasksIds.join("','") + "')";
      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] removeTasks tags success", result);
        }, (tx, error) => {
          console.error("[SQLITE] removeTasks tags Error has occured", error);
        }
      );
      if (environment.isElectron) {
        response.next(true);
      }
    }, (error) => {
      console.error("[SQLITE] removeTasks: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] removeTasks: Transaction success`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public completeTask(task: Task): Observable<any>{
    console.log("[SQLITE] completeTask, task: ", task);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] completeTask skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_STATUS_SQL, [TaskUtils.statusCompletedId(), "completed", taskJson, task.id], (tx, result) => {
        console.log("[SQLITE] completeTask success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] completeTask Error has occured", error);
        response.next(false);
      }
    );
    }, (error) => {
      console.error("[SQLITE] completeTask: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] completeTask: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updatePriority(task: Task, priority: number): Observable<any>{
    console.log("[SQLITE] updatePriority, task & priority: ", task, priority);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updatePriority skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_PRIORITY_SQL, [priority, taskJson, task.id], (tx, result) => {
        console.log("[SQLITE] updatePriority: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updatePriority: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updatePriority: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updatePriority: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateRepeat(task: Task, repeat: string): Observable<any>{
    console.log("[SQLITE] updateRepeat, task & repeat: ", task, repeat);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateRepeat skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_REPEAT_SQL, [repeat, taskJson, task.id], (tx, result) => {
        console.log("[SQLITE] updateRepeat: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updateRepeat: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updateRepeat: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateRepeat: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateStartDate(task: Task, startDate: Date): Observable<any>{
    console.log("[SQLITE] updateStartDate, task & startDate: ", task, startDate);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateStartDate skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_STARTDATE_SQL, [startDate ? TaskUtils.dateToSearchableDBDate(startDate) : null, taskJson, task.id], (tx, result) => {
        console.log("[SQLITE] updateStartDate: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updateStartDate: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updateStartDate: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateStartDate: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateDueDate(task: Task, dueDate: Date): Observable<any>{
    console.log("[SQLITE] updateDueDate, task & dueDate: ", task, dueDate);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateDueDate skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_DUEDATE_SQL, [dueDate ? TaskUtils.dateToSearchableDBDate(dueDate) : null, taskJson, task.id], (tx, result) => {
        console.log("[SQLITE] updateDueDate: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updateDueDate: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updateDueDate: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateDueDate: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateProjectId(task: Task, projectId: number): Observable<any>{
    console.log("[SQLITE] updateProjectId, task & projectId: ", task, projectId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateProjectId skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_PROJECTID_SQL, [projectId, taskJson, task.id], (tx, result) => {
        console.log("[SQLITE] updateProjectId: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updateProjectId: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updateProjectId: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateProjectId: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateList(tasks: Task[], listId: number): Observable<any>{
    console.log("[SQLITE] updateList, task & listId: ", tasks, listId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateList skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    // override tasks tags completely
    this.sqliteDB.transaction((tx) => {
      const query = this.UPDATE_TASKS_LIST_SQL + "('" + tasks.map(t => t.id).join("','") + "')";
      tx.executeSql(query, [listId], (tx, result) => {
        console.log("[SQLITE] updateList: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updateList: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updateList: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateList: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateLocation(tasks: Task[], locationId: number): Observable<any>{
    console.log("[SQLITE] updateList, task & locationId: ", tasks, locationId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateList skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    // override tasks tags completely
    this.sqliteDB.transaction((tx) => {
      const query = this.UPDATE_TASKS_LOCATION_SQL + "('" + tasks.map(t => t.id).join("','") + "')";
      tx.executeSql(query, [locationId], (tx, result) => {
        console.log("[SQLITE] updateLocation: success", result);
        response.next(true);
      }, (tx, error) => {
        console.error("[SQLITE] updateLocation: Error has occured", error);
        response.next(false);
      });
    }, (error) => {
      console.error("[SQLITE] updateLocation: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateLocation: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateAssignedTo(task: Task, assignedToId: number): Observable<any>{
    console.log("[SQLITE] updateAssignedTo, task & assignedToId: ", task, assignedToId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateAssignedTo skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    // override tasks tags completely
    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_ASSIGNEDTO_SQL, [assignedToId, taskJson, task.id],
        (tx, result) => {
          console.log("[SQLITE] updateAssignedTo, result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] updateAssignedTo, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] updateAssignedTo: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateAssignedTo: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateWatchers(task: Task, watcher_ids: number[]): Observable<any>{
    console.log("[SQLITE] updateWatchers, task & watcher_ids: ", task, watcher_ids);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateWatchers skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    // override tasks tags completely
    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      tx.executeSql(this.UPDATE_TASK_WATCHER_SQL, [ JSON.stringify(watcher_ids), taskJson, task.id],
        (tx, result) => {
          console.log("[SQLITE] updateWatchers, result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] updateWatchers, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] updateWatchers: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateWatchers: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateTags(task: Task, tags: ICompact[]): Observable<any>{
    console.log("[SQLITE] updateTags, task & tags: ", task, tags);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateTags skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    // override tasks tags completely
    this.sqliteDB.transaction((tx) => {
      const query = this.DELETE_TASK_TAG_BY_TASK_SQL + "('" + [task.id].join("','") + "')";
      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] updateTags, delete task result: ", result);
        }, (tx, error) => {
          console.error("[SQLITE] updateTags, error: ", error);
        }
      );
      setTimeout( () => {
        task.tags.forEach(tag => {
          tx.executeSql(this.CREATE_TAG_IF_NOT_EXIST_SQL, [tag.id, tag.name, tag.id], (tx, result) => {
            console.log(`[SQLITE] updateTags: Add or Update tag`, result);
          }, (tx, error) => {
            console.error(`[SQLITE] updateTags: Error has occured while updating task`, error);
          });
          tx.executeSql(this.CREATE_TASK_TAG_IF_NOT_EXIST_SQL, [task.id, tag.id, task.id, tag.id], (tx, result) => {
            console.log(`[SQLITE] updateTags: Add or Update task`, result);
          }, (tx, error) => {
            console.error(`[SQLITE] updateTags: Error has occured while updating task`, error);
          });
        });
      }, 200),

      tx.executeSql(this.UPDATE_TASK_TAGS_IDS_SQL, [JSON.stringify(task.tags.map(t  => t.id.toString())), task.id],
        (tx, result) => {
          console.log("[SQLITE] updateTags, update task result: ", result);
        }, (tx, error) => {
          console.error("[SQLITE] updateTags, error: ", error);
        }
      );
      if (environment.isElectron) {
         setTimeout(() => {
          response.next(true);
         }, 1000);
      }
    }, (error) => {
      console.error("[SQLITE] updateTags: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] updateTags: Transaction success`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public updateComments(task: Task): Observable<boolean>{
    console.log("[SQLITE] updateComments, task: ", task);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] updateComments skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      const taskJson = this.buildTaskJson(task);
      const params = [JSON.stringify(task.comments), taskJson, task.id];
      tx.executeSql(this.UPDATE_TASK_COMMENTS_SQL, params,
        (tx, result) => {
          console.log("[SQLITE] updateComments, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] updateComments, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] updateComments: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] updateComments: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public removeListFromTasks(listId: number): Observable<boolean>{
    console.log("[SQLITE] removeListFromTasks, listId: ", listId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] removeListFromTasks skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.UPDATE_TASK_REMOVE_LIST_SQL, [listId],
        (tx, result) => {
          console.log("[SQLITE] removeListFromTasks, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] removeListFromTasks, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] removeListFromTasks: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] removeListFromTasks: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public removeLocationFromTasks(locationId: number): Observable<boolean>{
    console.log("[SQLITE] removeLocationFromTasks, locationId: ", locationId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] removeLocationFromTasks skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.UPDATE_TASK_REMOVE_LOCATION_SQL, [locationId],
        (tx, result) => {
          console.log("[SQLITE] removeLocationFromTasks, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] removeLocationFromTasks, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] removeLocationFromTasks: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] removeLocationFromTasks: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }
  /// Lists

  public createOrUpdateLists(lists: List[]): Observable<boolean>{
    console.log("[SQLITE] createOrUpdateLists, lists: ", lists);

    const response = new Subject<boolean>();

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] createOrUpdateLists skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const maybeExistentIds = lists.map(list => list.id);

    let toCreateCounter = 0;
    let toUpdateCounter = 0;

    let time1;
    let time2;
    let time3;


    // Step 1:
    // find existing lists
    time1 = performance.now();
    console.log("[SQLITE] createOrUpdateLists: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] createOrUpdateLists: Transaction occured");
      time2 = performance.now();
      const query = this.FIND_LISTS_IDS_BY_IDS_SQL + "('" + maybeExistentIds.join("','") + "')";

      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] createOrUpdateLists found lists in DB: ", result.rows.length);

          let existentIds = [];
          for (let i = 0; i < result.rows.length; i++) {
            let item = result.rows.item(i);
            existentIds.push(item.id);
          }

          // Step 2: insert/update tasks in DB
          //
          maybeExistentIds.forEach((id, index) => {
            let query;
            let params;

            const list = lists[index];

            if (existentIds.includes(id)) {
              params = [list.name, list.id];
              query = this.UPDATE_LIST_SQL;
              ++toUpdateCounter;
            } else {
              params = [list.id, list.name];
              query = this.CREATE_LIST_SQL;
              ++toCreateCounter;
            }

            // write to DB
            tx.executeSql(query, params, (tx, result) => {
              //
            }, (tx, error) => {
              console.error(`[SQLITE] createOrUpdateLists: Error has occured while saving task`, error);
            });
          });
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] createOrUpdateLists: Error has occured while finding tasks", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] createOrUpdateLists: Transaction error", error);
      response.next(false);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] createOrUpdateLists: Transaction success. Created: ${toCreateCounter}, Updated: ${toUpdateCounter}. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public getListsWithTasksCount(): Observable<any>{
    console.log("[SQLITE] getListsWithTasksCount");

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getListsWithTasksCount skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_ALL_LISTS_WITH_TASKS_COUNT_SQL, [],
        (tx, result) => {
          console.log("[SQLITE] getListsWithTasksCount result", result.rows);
          let lists: List[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              lists.push(new List(item));
            }
          }
          console.log("[SQLITE] getListsWithTasksCount: success", lists);
          response.next(lists);
        }, (tx, error) => {
          console.error("[SQLITE] getListsWithTasksCount: error has occured while groupping lists", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getListsWithTasksCount: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getListsWithTasksCount: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public deleteList(id: number): Observable<boolean>{
    console.log("[SQLITE] deleteList, id: " + id);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] deleteList skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.DELETE_LIST_SQL, [id],
        (tx, result) => {
          console.log("[SQLITE] deleteList, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] deleteList, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] deleteList: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] deleteList: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }
  /// Locations

  public createOrUpdateLocations(locations: Location[]): Observable<boolean>{
    console.log("[SQLITE] createOrUpdateLocations, lists: ", locations);

    const response = new Subject<boolean>();

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] createOrUpdateLocations skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const maybeExistentIds = locations.map(location => location.id);

    let toCreateCounter = 0;
    let toUpdateCounter = 0;

    let time1;
    let time2;
    let time3;


    // Step 1:
    // find existing locations
    time1 = performance.now();
    console.log("[SQLITE] createOrUpdateLocations: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] createOrUpdateLocations: Transaction occured");
      time2 = performance.now();
      const query = this.FIND_LOCATIONS_IDS_BY_IDS_SQL + "('" + maybeExistentIds.join("','") + "')";

      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] createOrUpdateLocations found lists in DB: ", result.rows.length);

          let existentIds = [];
          for (let i = 0; i < result.rows.length; i++) {
            let item = result.rows.item(i);
            existentIds.push(item.id);
          }

          // Step 2: insert/update tasks in DB
          //
          maybeExistentIds.forEach((id, index) => {
            let query;
            let params;

            const location = locations[index];

            if (existentIds.includes(id)) {
              params = [location.name, location.id];
              query = this.UPDATE_LOCATION_SQL;
              ++toUpdateCounter;
            } else {
              params = [location.id, location.name];
              query = this.CREATE_LOCATION_SQL;
              ++toCreateCounter;
            }

            // write to DB
            tx.executeSql(query, params, (tx, result) => {
              //
            }, (tx, error) => {
              console.error(`[SQLITE] createOrUpdateLocations: Error has occured while saving task`, error);
            });
          });
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] createOrUpdateLocations: Error has occured while finding tasks", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] createOrUpdateLocations: Transaction error", error);
      response.next(false);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] createOrUpdateLocations: Transaction success. Created: ${toCreateCounter}, Updated: ${toUpdateCounter}. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public getLocationsWithTasksCount(): Observable<any>{
    console.log("[SQLITE] getLocationsWithTasksCount");

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getLocationsWithTasksCount skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<any>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_ALL_LOCATIONS_WITH_TASKS_COUNT_SQL, [],
        (tx, result) => {
          console.log("[SQLITE] getLocationsWithTasksCount result", result.rows);
          let locations: Location[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              locations.push(new Location(item));
            }
          }
          console.log("[SQLITE] getLocationsWithTasksCount: success", locations);
          response.next(locations);
        }, (tx, error) => {
          console.error("[SQLITE] getLocationsWithTasksCount: error has occured while groupping lists", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getLocationsWithTasksCount: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getLocationsWithTasksCount: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public deleteLocation(id: number): Observable<boolean>{
    console.log("[SQLITE] deleteLocation, id: " + id);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] deleteLocation skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.DELETE_LOCATION_SQL, [id],
        (tx, result) => {
          console.log("[SQLITE] deleteLocation, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] deleteLocation, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] deleteLocation: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] deleteLocation: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }
  /// Projects & members

  public getAllProjects(): Observable<Project[]>{
    console.log("[SQLITE] getAllProjects");

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getAllProjects skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<Project[]>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_ALL_PROJECT_SQL, [],
        (tx, result) => {
          let projects: Project[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              const item = result.rows.item(i);
              projects.push(new Project(JSON.parse(item.project)));
            }
          }
          console.log("[SQLITE] getAllProjects fetched projects: ", projects);

          response.next(projects);
        }, (tx, error) => {
          console.log("[SQLITE] getAllProjects Error has occured while finding projects", error);
          response.next(undefined);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getAllProjects: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getAllProjects: Transaction success`);
    });

    return response.asObservable().pipe(take(1));
  }

  public createOrUpdateProjects(projects: Project[], membersHash: any): Observable<boolean>{
    console.log("[SQLITE] createOrUpdateProjects, projects: ", projects.length);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] createOrUpdateProjects skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    if (!projects || projects.length === 0) {
      return;
    }

    const maybeExistentIds = projects.map(list => list.id);

    let toCreateCounter = 0;
    let toUpdateCounter = 0;

    let time1;
    let time2;
    let time3;

    const response = new Subject<boolean>();

    // Step 1:
    // find existing projects
    time1 = performance.now();
    console.log("[SQLITE] createOrUpdateProjects: Trying to accure transaction");
    this.sqliteDB.transaction((tx) => {
      console.log("[SQLITE] createOrUpdateProjects: Transaction occured");
      time2 = performance.now();
      const query = this.FIND_PROJECTS_IDS_BY_IDS_SQL + "('" + maybeExistentIds.join("','") + "')";

      tx.executeSql(query, [],
        (tx, result) => {
          console.log("[SQLITE] createOrUpdateProjects found projects in DB: ", result.rows.length);

          let existentIds = [];
          for (let i = 0; i < result.rows.length; i++) {
            let item = result.rows.item(i);
            existentIds.push(item.id);
          }

          // Step 2: insert/update tasks in DB
          //
          maybeExistentIds.forEach((id, index) => {
            let query;
            let params;

            const project = projects[index];
            const members = membersHash[project.id];

            const unifiedParams = [JSON.stringify(project), JSON.stringify(members)];

            if (existentIds.includes(id)) {
              params = [...unifiedParams, project.id];
              query = this.UPDATE_PROJECT_SQL;
              ++toUpdateCounter;
            } else {
              params = [project.id, ...unifiedParams];
              query = this.CREATE_PROJECT_SQL;
              ++toCreateCounter;
            }

            // write to DB
            tx.executeSql(query, params, (tx, result) => {
              //
            }, (tx, error) => {
              console.error(`[SQLITE] createOrUpdateProjects: Error has occured while saving project`, error);
            });
          });
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] createOrUpdateProjects: Error has occured while finding projects", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] createOrUpdateProjects: Transaction error", error);
      response.next(false);
    }, () => {
      time3 = performance.now();
      console.log(`[SQLITE] createOrUpdateProjects: Transaction success. Created: ${toCreateCounter}, Updated: ${toUpdateCounter}. TTAT ${(time2 - time1).toFixed()} TTFT ${(time3 - time2).toFixed()} Total ${(time3 - time1).toFixed()}`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public getProjectMembers(projectId: number): Observable<User[]>{
    console.log("[SQLITE] getProjectMembers, id: " + projectId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getProjectMembers skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<User[]>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.FIND_PROJECT_MEMBERS_SQL, [projectId],
        (tx, result) => {
          if (result.rows.length === 1) {
            let item = result.rows.item(0);
            let jsonMembers = JSON.parse(item.members);
            let members: User[] = [];
            for (let i = 0; i < jsonMembers.length; i++) {
              members.push(new User(jsonMembers[i]));
            }
            console.log("[SQLITE] getProjectMembers fetched members: ", members);
            response.next(members);
          } else {
            console.log("[SQLITE] getProjectMembers, nothing found");
            response.next(undefined);
          }
        }, (tx, error) => {
          console.error("[SQLITE] getProjectMembers: Error1 has occured while executing sql", error);
          response.next(undefined);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getProjectMembers: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] getProjectMembers: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }
  /// Pendings

  public addPendingTaskDelete(taskId: number): Observable<boolean>{
    console.log("[SQLITE] addPendingTaskDelete", taskId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addPendingTaskDelete skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    // need to check maybe we already have a pending task create operation
    // if yes - remove it only

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.FIND_PENDING_OPERATION_ID_BY_ID_SQL, [taskId],
        (tx, result) => {
          if (result.rows.length > 0) {
            for (let i = 0; i < result.rows.length; i++) {
              const item = result.rows.item(i);
              const pendingTaskId = item.task_id;

              console.log("[SQLITE] addPendingTaskDelete: found pending op, so removing it. Id:", pendingTaskId);
              tx.executeSql(this.DELETE_PENDING_OPERATION_SQL, [pendingTaskId],
                (tx, result) => {
                  console.log("[SQLITE] addPendingTaskDelete, success result: ", result);
                }, (tx, error) => {
                  console.error("[SQLITE] addPendingTaskDelete, error: ", error);
                }
              );
            }
          } else {
            console.log("[SQLITE] addPendingTaskDelete: not found pending create op");

            // not exist then create a pending delete operation
            tx.executeSql(this.ADD_PENDING_OPERATION_SQL, [taskId, PendingOperation.PENDING_OPERATION_TYPE_TASK_DELETE, null],
              (tx, result) => {
                console.log("[SQLITE] addPendingTaskDelete success", result);
              }, (tx, error) => {
                console.error("[SQLITE] addPendingTaskDelete: Error2 has occured while executing sql", error);
              }
            );
          }
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] addPendingTaskDelete: Error1 has occured while executing sql", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] addPendingTaskDelete: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] addPendingTaskDelete: Transaction success.`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public addPendingTaskCreate(taskId: number): Observable<boolean>{
    console.log("[SQLITE] addPendingTaskCreate", taskId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addPendingTaskCreate skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.ADD_PENDING_OPERATION_SQL, [taskId, PendingOperation.PENDING_OPERATION_TYPE_TASK_CREATE, null],
        (tx, result) => {
          console.log("[SQLITE] addPendingTaskCreate, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] addPendingTaskCreate, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] addPendingTaskCreate: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] addPendingTaskCreate: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  public addPendingTaskUpdate(taskId: number, fileUploads: any = null, repeat: String = null, watcherMembers: any = null): Observable<boolean>{
    console.log("[SQLITE] addPendingTaskUpdate", taskId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addPendingTaskUpdate skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    let additionalData = null;
    if (fileUploads || repeat || watcherMembers) {
      additionalData = {};
      if (fileUploads) {
        additionalData["fileUploads"] = fileUploads;
      }
      if (repeat) {
        additionalData["repeat"] = repeat;
      }
      if (watcherMembers) {
        additionalData["watcherMembers"] = watcherMembers;
      }
      additionalData = JSON.stringify(additionalData);
    }

    const response = new Subject<boolean>();

    // need to check maybe we already have a pending task create operation
    // if yes - then ignore current one, do not create new

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.FIND_PENDING_OPERATION_ID_BY_ID_SQL, [taskId],
        (tx, result) => {
          if (result.rows.length > 0) {
            const item = result.rows.item(0);
            const pendingTaskId = item.task_id;

            console.log("[SQLITE] addPendingTaskUpdate: found pending create op, so ignore current. Id:", pendingTaskId);

            if (additionalData) {
              console.log("[SQLITE] addPendingTaskUpdate: add additionalData to op Id:", pendingTaskId);
              console.log("[SQLITE] addPendingTaskUpdate: additionalData", additionalData);

              tx.executeSql(this.UPDATE_PENDING_OPERATION_ADDITIONALDATA_SQL, [additionalData, taskId],
                (tx, result) => {
                  console.log("[SQLITE] addPendingTaskUpdate add fileUploads to op Id success", result);
                }, (tx, error) => {
                  console.error("[SQLITE] addPendingTaskUpdate: Error3 has occured while executing sql", error);
                }
              );
            }
          } else {
            console.log("[SQLITE] addPendingTaskUpdate: not found pending create op");

            // not exist then create a pending update operation
            tx.executeSql(this.ADD_PENDING_OPERATION_SQL, [taskId, PendingOperation.PENDING_OPERATION_TYPE_TASK_UPDATE, additionalData],
              (tx, result) => {
                console.log("[SQLITE] addPendingTaskUpdate success", result);
              }, (tx, error) => {
                console.error("[SQLITE] addPendingTaskUpdate: Error2 has occured while executing sql", error);
              }
            );
          }
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] addPendingTaskUpdate: Error1 has occured while executing sql", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] addPendingTaskUpdate: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] addPendingTaskUpdate: Transaction success.`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public addPendingTaskCommentCreate(taskId: number, taskComment: TaskComment): Observable<boolean>{
    console.log("[SQLITE] addPendingTaskCommentCreate", taskId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addPendingTaskCommentCreate skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }
    let commentPaylod: any = taskComment;
    commentPaylod.task_id = taskId;

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.ADD_PENDING_OPERATION_SQL, [taskComment.id, PendingOperation.PENDING_OPERATION_TYPE_COMMENT_ADD, JSON.stringify(commentPaylod)],
        (tx, result) => {
          console.log("[SQLITE] AddPendingTaskCommentCreate, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] AddPendingTaskCommentCreate, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] addPendingTaskCommentCreate: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] addPendingTaskCommentCreate: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  public addPendingTaskCommentUpdate(taskId: number, taskComment: TaskComment): Observable<boolean>{
    console.log("[SQLITE] addPendingTaskCommentUpdate", taskId);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addPendingTaskCommentUpdate skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    let commentPaylod: any = taskComment;
    commentPaylod.task_id = taskId;

    this.sqliteDB.transaction((tx) => {
      tx.executeSql(this.FIND_PENDING_OPERATION_ID_BY_ID_SQL, [commentPaylod.id],
        (tx, result) => {
          if (result.rows.length > 0) {
            const item = result.rows.item(0);
            const pendingTaskId = item.task_id;

            console.log("[SQLITE] addPendingTaskCommentUpdate: found pending create op, so ignore current. Id:", pendingTaskId);

            if (commentPaylod) {
              console.log("[SQLITE] addPendingTaskCommentUpdate: add additionalData to op Id:", pendingTaskId);

              tx.executeSql(this.UPDATE_PENDING_OPERATION_ADDITIONALDATA_SQL, [commentPaylod.id, JSON.stringify(commentPaylod)],
                (tx, result) => {
                  console.log("[SQLITE] addPendingTaskCommentUpdate add fileUploads to op Id success", result);
                }, (tx, error) => {
                  console.error("[SQLITE] addPendingTaskCommentUpdate: Error3 has occured while executing sql", error);
                }
              );
            }
          } else {
            console.log("[SQLITE] addPendingTaskCommentUpdate: not found pending create op");

            // not exist then create a pending update operation
            tx.executeSql(this.ADD_PENDING_OPERATION_SQL, [commentPaylod.id, PendingOperation.PENDING_OPERATION_TYPE_COMMENT_EDIT, JSON.stringify(commentPaylod)],
              (tx, result) => {
                console.log("[SQLITE] addPendingTaskCommentUpdate success", result);
              }, (tx, error) => {
                console.error("[SQLITE] addPendingTaskCommentUpdate: Error2 has occured while executing sql", error);
              }
            );
          }
          if (environment.isElectron) {
            response.next(true);
          }
        }, (tx, error) => {
          console.error("[SQLITE] addPendingTaskCommentUpdate: Error1 has occured while executing sql", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] addPendingTaskCommentUpdate: Transaction error", error);
      response.next(false);
    }, () => {
      console.log(`[SQLITE] addPendingTaskCommentUpdate: Transaction success.`);
      response.next(true);
    });

    return response.asObservable().pipe(take(1));
  }

  public addPendingAttachmentsBulkDelete(taskId: number, attachmentsIds: number[]): Observable<boolean>{
    console.log("[SQLITE] addPendingAttachmentsBulkDelete", taskId, attachmentsIds);

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] addPendingAttachmentsBulkDelete skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<boolean>();

    this.sqliteDB.transaction((tx) => {
      const additionalData = {"attachmentsIds": attachmentsIds};
      tx.executeSql(this.ADD_PENDING_OPERATION_SQL, [taskId, PendingOperation.PENDING_OPERATION_TYPE_ATTACHMENTS_BULK_DELETE, JSON.stringify(additionalData)],
        (tx, result) => {
          console.log("[SQLITE] addPendingAttachmentBulkDelete, success result: ", result);
          response.next(true);
        }, (tx, error) => {
          console.error("[SQLITE] addPendingAttachmentBulkDelete, error: ", error);
          response.next(false);
        }
      );
    }, (error) => {
      console.error("[SQLITE] addPendingAttachmentsBulkDelete: Transaction error", error);
    }, () => {
      console.log(`[SQLITE] addPendingAttachmentsBulkDelete: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

  public getAllPendingOperations(): Observable<PendingOperation[]>{
    console.log("[SQLITE] getAllPendingOperations");

    if (!this.sqliteDB || !this.isDbInitialized) {
      console.log("[SQLITE] getAllPendingOperations skip: sqliteDB or isDbInitialized is not initialized.");
      return;
    }

    const response = new Subject<PendingOperation[]>();

    this.sqliteDB.readTransaction((tx) => {
      tx.executeSql(this.SELECT_ALL_PENDING_OPERATIONS_SQL, [],
        (tx, result) => {
          let ops: PendingOperation[] = [];
          if (result.rows) {
            for (let i = 0; i < result.rows.length; i++) {
              let item = result.rows.item(i);
              ops.push(new PendingOperation(item));
            }
          }
          console.log("[SQLITE] getAllPendingOperations fetched ops: ", ops);

          response.next(ops);
        }, (tx, error) => {
          console.error("[SQLITE] getAllPendingOperations: Error has occured while executing sql", error);
          response.next(null);
        }
      );
    }, (error) => {
      console.error("[SQLITE] getAllPendingOperations: Transaction error", error);
      response.next(null);
    }, () => {
      console.log(`[SQLITE] getAllPendingOperations: Transaction success.`);
    });

    return response.asObservable().pipe(take(1));
  }

}
